import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Card, List, Button, Input, Space, Typography, Tag, Empty, Spin } from 'antd';
import { SoundOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useSearchParams, Link, useParams, useNavigate, useLocation } from 'react-router-dom';
import DateRangeSelector, { DateRange } from '../components/DateRangeSelector';
import AudioPlayer from '../components/AudioPlayer';
import AudioTranscriptionEditor from '../components/AudioTranscriptionEditor';
import JobAudioPlayer from '../components/JobAudioPlayer';
import GroupedSegmentAudioPlayer from '../components/GroupedSegmentAudioPlayer';
import SegmentAudioPlayer from '../components/SegmentAudioPlayer';
import { useDevice } from '../contexts/DeviceContext';
import { API_CONFIG } from '../config/api';
import { secureLogger } from '../utils/secureLogger';
import { DEFAULT_HIGHLIGHT_STYLE, highlightText } from '../utils/highlightText';
import { formatJstDateTime } from '../utils/dateTime';
import {
  getWhisperSegmentSummary,
  getWhisperTranscriptMessage,
  hasRecognizedSpeech,
  isNoSpeechDetected,
  isWhisperBatchEngine,
} from '../utils/whisperTranscriptState';
import { AudioSeries, AudioSeriesListResponse, TranscriptDetail, TranscriptSegment } from '../types/api';

const { Text, Title } = Typography;
const { Search } = Input;

interface AudioData {
  id: string;
  start_time: string;
  received_at: string;
  duration_ms: number;
  codec: string;
  sampling_rate: number;
  bit_depth: number;
  bitrate: number;
  channels: number;
  file_size: number;
  status: string;
}

type SegmentSlice = {
  audio_id: string;
  start_offset_ms?: number;
  end_offset_ms?: number;
  order_index?: number;
};

type NormalizedSegment = {
  key: string;
  startSec: number;
  endSec: number;
  text?: string;
  speaker?: string;
  speakerConf?: number;
  audioId: string;
  clipSlices?: Array<{
    audioId: string;
    startSec: number;
    endSec: number;
  }>;
  isPartialSlice?: boolean;
};

type AudioJobRow = {
  key: string;
  currentJobId?: string | null;
  startedAt: string;
  endedAt: string;
  codec: string;
  sourceAudios: AudioData[];
  audioIds: string[];
  durationMs: number;
  transcriptDetail?: TranscriptDetail | null;
  isGrouped: boolean;
  runCount: number;
};

const getSegmentSlices = (segment: TranscriptSegment): SegmentSlice[] =>
  segment.audio_slices ?? segment.slices ?? [];

const DEFAULT_PAGE_SIZE = 10;
const MAX_PAGE_SIZE = 10;

const AudioPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { deviceId: routeDeviceId } = useParams<{ deviceId?: string }>();
  const navigate = useNavigate();
  const { devices, selectedDevice, setSelectedDevice } = useDevice();
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [showSpinner, setShowSpinner] = useState(false);
  const [jobRows, setJobRows] = useState<AudioJobRow[]>([]);
  const [transcriptsFilter, setTranscriptsFilter] = useState<'all' | 'with' | 'without'>('all');
  const skipSearchSyncRef = useRef(false);
  const lastSearchTextRef = useRef('');
  const latestRangeRef = useRef<DateRange | null>(null);
  const latestPageSizeRef = useRef<number>(DEFAULT_PAGE_SIZE);
  const [audioTotal, setAudioTotal] = useState(0);
  const [deviceNotFound, setDeviceNotFound] = useState(false);
  const activeDeviceId = routeDeviceId ?? selectedDevice?.id ?? null;

  const searchParamString = useMemo(() => location.search, [location.search]);

  const parseRangeFromParams = useCallback((): DateRange | null => {
    const params = new URLSearchParams(searchParamString);
    const startParam = params.get('start_time');
    const endParam = params.get('end_time');
    if (startParam && endParam && !Number.isNaN(Date.parse(startParam)) && !Number.isNaN(Date.parse(endParam))) {
      return { start: new Date(startParam).toISOString(), end: new Date(endParam).toISOString() };
    }
    return null;
  }, [searchParamString]);

  const parseNumberParam = useCallback(
    (key: string, fallback: number, min = 1) => {
      const params = new URLSearchParams(searchParamString);
      const value = Number(params.get(key));
      if (Number.isFinite(value) && value >= min) {
        return Math.min(Math.floor(value), MAX_PAGE_SIZE);
      }
      return fallback;
    },
    [searchParamString],
  );

  const parseStringParam = useCallback(
    (key: string, fallback = '') => {
      const params = new URLSearchParams(searchParamString);
      const value = params.get(key);
      return value ?? fallback;
    },
    [searchParamString],
  );

  const [currentRange, setCurrentRange] = useState<DateRange | null>(() => parseRangeFromParams());
  const [pageSize, setPageSize] = useState<number>(() => parseNumberParam('page_size', DEFAULT_PAGE_SIZE));
  const [currentPage, setCurrentPage] = useState<number>(() => parseNumberParam('page', 1));
  const [isParamsInitialized, setParamsInitialized] = useState(false);

  useEffect(() => {
    if (!routeDeviceId) {
      setDeviceNotFound(false);
      return;
    }
    if (devices.length === 0) {
      // デバイス一覧のロード待ち
      setDeviceNotFound(false);
      return;
    }
    const match = devices.find((device) => device.id === routeDeviceId) || null;
    if (match) {
      setDeviceNotFound(false);
      if (selectedDevice?.id !== match.id) {
        setSelectedDevice(match);
      }
    } else {
      setDeviceNotFound(true);
    }
  }, [routeDeviceId, devices, selectedDevice, setSelectedDevice]);

  const hydrateJobDetails = useCallback(async (deviceId: string, jobs: AudioJobRow[], token: string) => {
    const targets = jobs.filter((job) => job.transcriptDetail?.id && !job.transcriptDetail?.segments);
    if (targets.length === 0) {
      return;
    }

    const details = await Promise.all(
      targets.map(async (job) => {
        try {
          const response = await fetch(
            `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/audio-series/${job.key}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );
          if (!response.ok) {
            secureLogger.error('Failed to hydrate transcript detail', {
              jobId: job.key,
              transcriptId: job.transcriptDetail?.id,
              status: response.status,
            });
            return null;
          }
          const detailJob = await response.json() as AudioSeries;
          return { key: job.key, detail: detailJob.transcript };
        } catch (error) {
          secureLogger.error('Transcript detail hydration threw error', {
            jobId: job.key,
            transcriptId: job.transcriptDetail?.id,
          });
          return null;
        }
      }),
    );

    setJobRows((prev) => prev.map((job) => {
      const matched = details.find((detail) => detail?.key === job.key);
      return matched ? { ...job, transcriptDetail: matched.detail } : job;
    }));
  }, []);

  const fetchAudioData = useCallback(async (deviceId: string, range: DateRange | null, page: number, size: number, searchQuery: string) => {
    secureLogger.debug('fetchAudioData called', { range, deviceId, page, size });
    if (!deviceId) {
      secureLogger.error('No device selected for audio range request');
      return;
    }
    
    setLoading(true);
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) {
        secureLogger.error('No token for audio range request');
        return;
      }
      
      const limit = size > 0 ? size : DEFAULT_PAGE_SIZE;
      const offset = Math.max(0, (page - 1) * limit);
      const params = new URLSearchParams();
      if (range?.start && range?.end) {
        params.set('from_time', range.start);
        params.set('to_time', range.end);
      }
      if (searchQuery) {
        params.set('q', searchQuery);
      }
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      const query = params.toString();
      const url = `${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/audio-series${query ? `?${query}` : ''}`;
      secureLogger.debug('Making audio API request', { deviceId, url });
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      secureLogger.debug('Audio API response', { status: response.status });
      
      if (response.ok) {
        const data = await response.json() as AudioSeriesListResponse;
        const jobs = (data.series || []).map<AudioJobRow>((series: AudioSeries) => ({
          key: series.id,
          currentJobId: series.current_job_id,
          startedAt: series.started_at,
          endedAt: series.ended_at,
          codec: series.codec,
          sourceAudios: (series.source_audios || []) as AudioData[],
          audioIds: series.audio_ids || [],
          durationMs: series.duration_ms,
          transcriptDetail: series.transcript,
          isGrouped: series.is_grouped,
          runCount: series.run_count,
        }));
        secureLogger.debug('Transcript jobs API response data', { dataKeys: Object.keys(data), jobsLength: jobs.length });
        setJobRows(jobs);
        setAudioTotal(data.total ?? jobs.length);
        void hydrateJobDetails(deviceId, jobs, token);
      } else {
        secureLogger.error('Audio range API failed', { status: response.status });
      }
    } catch (error) {
      secureLogger.error('Failed to fetch audio data', { error: 'Audio fetch failed' });
    } finally {
      setLoading(false);
    }
  }, [hydrateJobDetails]);

  const updateQueryParams = useCallback(
    (range: DateRange | null, page: number, size: number, searchQuery: string) => {
      const params = new URLSearchParams(searchParamString);
      let changed = false;
      if (range) {
        if (params.get('start_time') !== range.start) {
          params.set('start_time', range.start);
          changed = true;
        }
        if (params.get('end_time') !== range.end) {
          params.set('end_time', range.end);
          changed = true;
        }
      } else {
        if (params.has('start_time')) {
          params.delete('start_time');
          changed = true;
        }
        if (params.has('end_time')) {
          params.delete('end_time');
          changed = true;
        }
      }
      if (params.get('page') !== String(page)) {
        params.set('page', String(page));
        changed = true;
      }
      if (params.get('page_size') !== String(size)) {
        params.set('page_size', String(size));
        changed = true;
      }
      if (searchQuery) {
        if (params.get('q') !== searchQuery) {
          params.set('q', searchQuery);
          changed = true;
        }
      } else if (params.has('q')) {
        params.delete('q');
        changed = true;
      }
      if (changed) {
        setSearchParams(params, { replace: true });
      }
    },
    [searchParamString, setSearchParams],
  );

  useEffect(() => {
    if (!activeDeviceId || isParamsInitialized) {
      return;
    }
    const initialRange = parseRangeFromParams();
    const initialPage = parseNumberParam('page', 1);
    const initialSize = parseNumberParam('page_size', DEFAULT_PAGE_SIZE);
    setCurrentRange(initialRange);
    setCurrentPage(initialPage);
    setPageSize(initialSize);
    setParamsInitialized(true);
  }, [activeDeviceId, isParamsInitialized, parseRangeFromParams, parseNumberParam]);

  useEffect(() => {
    latestRangeRef.current = currentRange;
  }, [currentRange]);

  useEffect(() => {
    latestPageSizeRef.current = pageSize;
  }, [pageSize]);

  // デバイス変更時にデータをリセット
  useEffect(() => {
    setJobRows([]);
  }, [activeDeviceId]);

  useEffect(() => {
    if (!isParamsInitialized) {
      return;
    }
    const rangeFromUrl = parseRangeFromParams();
    const pageFromUrl = parseNumberParam('page', 1);
    const sizeFromUrl = parseNumberParam('page_size', DEFAULT_PAGE_SIZE);
    const searchFromUrl = parseStringParam('q');

    setCurrentRange((prev) => {
      const prevStart = prev?.start ?? null;
      const prevEnd = prev?.end ?? null;
      const nextStart = rangeFromUrl?.start ?? null;
      const nextEnd = rangeFromUrl?.end ?? null;
      if (prevStart === nextStart && prevEnd === nextEnd) {
        return prev;
      }
      return rangeFromUrl;
    });
    setCurrentPage((prev) => (prev === pageFromUrl ? prev : pageFromUrl));
    setPageSize((prev) => (prev === sizeFromUrl ? prev : sizeFromUrl));
    setSearchText((prev) => {
      if (prev === searchFromUrl) {
        return prev;
      }
      skipSearchSyncRef.current = true;
      return searchFromUrl;
    });
    setAppliedSearch((prev) => (prev === searchFromUrl ? prev : searchFromUrl));
  }, [searchParamString, isParamsInitialized, parseRangeFromParams, parseNumberParam, parseStringParam]);

  useEffect(() => {
    if (!isParamsInitialized || !activeDeviceId) {
      return;
    }
    fetchAudioData(activeDeviceId, currentRange, currentPage, pageSize, appliedSearch);
  }, [activeDeviceId, currentRange, currentPage, pageSize, appliedSearch, isParamsInitialized, fetchAudioData]);

  const handleModeChange = useCallback((mode: 'preset' | 'custom') => {
    secureLogger.debug('Audio date range mode changed', { mode });
  }, []);

  const handleDateRangeChange = useCallback(
    (range: DateRange | null) => {
      secureLogger.debug('handleDateRangeChange called', { range, deviceId: activeDeviceId });
      setCurrentRange(range);
      setCurrentPage(1);
      if (activeDeviceId && isParamsInitialized) {
        updateQueryParams(range, 1, pageSize, appliedSearch);
      }
    },
    [pageSize, activeDeviceId, updateQueryParams, isParamsInitialized, appliedSearch],
  );

  const handlePaginationChange = useCallback(
    (page: number, size: number) => {
      setCurrentPage(page);
      setPageSize(size);
      if (isParamsInitialized) {
        updateQueryParams(currentRange, page, size, appliedSearch);
      }
    },
    [currentRange, updateQueryParams, isParamsInitialized, appliedSearch],
  );

  useEffect(() => {
    if (!isParamsInitialized) {
      return;
    }
    if (skipSearchSyncRef.current) {
      skipSearchSyncRef.current = false;
      return;
    }
    if (lastSearchTextRef.current === searchText) {
      return;
    }
    lastSearchTextRef.current = searchText;
    const handler = window.setTimeout(() => {
      setAppliedSearch(searchText.trim());
      setCurrentPage(1);
      updateQueryParams(
        latestRangeRef.current,
        1,
        latestPageSizeRef.current,
        searchText.trim(),
      );
    }, 300);
    return () => window.clearTimeout(handler);
  }, [searchText, updateQueryParams, isParamsInitialized]);

  useEffect(() => {
    let t: number | undefined;
    if (loading) {
      t = window.setTimeout(() => setShowSpinner(true), 300);
    } else {
      setShowSpinner(false);
    }
    return () => { if (t) clearTimeout(t); };
  }, [loading]);

  const filteredData = useMemo(() => {
    return jobRows.filter((item) => {
      const transcriptDetail = item.transcriptDetail;
      if (transcriptsFilter === 'with' && !hasRecognizedSpeech(transcriptDetail)) {
        return false;
      }
      if (transcriptsFilter === 'without' && hasRecognizedSpeech(transcriptDetail)) {
        return false;
      }
      return true;
    });
  }, [jobRows, transcriptsFilter]);

  const renderFormatTag = useCallback(
    (row: AudioJobRow) => {
      const engine = row.transcriptDetail?.engine;
      if (!engine) {
        return null;
      }
      const isWhisper = isWhisperBatchEngine(engine);
      return <Tag color={isWhisper ? 'purple' : 'default'}>{isWhisper ? '新形式' : '旧形式'}</Tag>;
    },
    [],
  );

  const renderJobStructure = useCallback((row: AudioJobRow) => {
    if (row.sourceAudios.length <= 1) {
      return null;
    }

    return (
      <div
        style={{
          marginTop: 12,
          padding: 12,
          borderRadius: 10,
          border: '1px solid #d8e4ff',
          background: '#f7faff',
        }}
      >
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Space wrap size={[8, 8]}>
            <Tag color="blue">Series</Tag>
            <Text code copyable={{ text: row.key }}>{row.key}</Text>
          </Space>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {row.sourceAudios.map((audio, index) => (
              <div
                key={audio.id}
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: '#ffffff',
                  border: '1px solid #eef2f6',
                }}
              >
                <Tag color="default">#{index + 1}</Tag>
                <Text strong>{formatJstDateTime(audio.start_time)}</Text>
                <Text type="secondary">{(audio.duration_ms / 1000).toFixed(1)}秒</Text>
                <Text code style={{ fontSize: 12 }}>{audio.id}</Text>
              </div>
            ))}
          </div>
        </Space>
      </div>
    );
  }, []);

  const buildSegmentsForRow = useCallback(
    (detail: TranscriptDetail | undefined | null, row: AudioJobRow): NormalizedSegment[] => {
      if (!detail?.segments?.length) {
        return [];
      }

      if (row.isGrouped) {
        return detail.segments
          .map<NormalizedSegment | null>((segment: TranscriptSegment, idx: number) => {
            if (segment.start_ms == null || segment.end_ms == null) {
              return null;
            }
            return {
              key: `${segment.id ?? row.key}-${idx}`,
              startSec: Math.max(0, segment.start_ms / 1000),
              endSec: Math.max(segment.start_ms / 1000, segment.end_ms / 1000),
              text: segment.text,
              speaker: segment.speaker_label,
              speakerConf: segment.speaker_confidence ?? segment.segment_confidence,
              audioId: row.audioIds[0] ?? '',
              clipSlices: getSegmentSlices(segment)
                .filter((slice) => slice.start_offset_ms != null && slice.end_offset_ms != null)
                .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0))
                .map((slice) => ({
                  audioId: slice.audio_id,
                  startSec: Math.max(0, (slice.start_offset_ms ?? 0) / 1000),
                  endSec: Math.max((slice.start_offset_ms ?? 0) / 1000, (slice.end_offset_ms ?? 0) / 1000),
                })),
            };
          })
          .filter((seg): seg is NormalizedSegment => Boolean(seg))
          .sort((a, b) => a.startSec - b.startSec);
      }

      return detail.segments.flatMap((segment: TranscriptSegment, idx: number) => {
        const slices = getSegmentSlices(segment)
          .filter((slice) => slice.audio_id === row.audioIds[0])
          .sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

        if (slices.length > 0) {
          return slices.map<NormalizedSegment | null>((slice, sliceIndex) => {
            if (slice.start_offset_ms == null || slice.end_offset_ms == null) {
              return null;
            }
            return {
              key: `${segment.id ?? row.key}-${idx}-${sliceIndex}`,
              startSec: Math.max(0, slice.start_offset_ms / 1000),
              endSec: Math.max(slice.start_offset_ms / 1000, slice.end_offset_ms / 1000),
              text: segment.text,
              speaker: segment.speaker_label,
              speakerConf: segment.speaker_confidence ?? segment.segment_confidence,
              audioId: row.audioIds[0] ?? '',
              isPartialSlice: slices.length > 1,
            };
          }).filter((seg): seg is NormalizedSegment => Boolean(seg));
        }

        if (segment.start_ms == null || segment.end_ms == null) {
          return [];
        }

        return [{
          key: `${segment.id ?? row.key}-${idx}`,
          startSec: Math.max(0, segment.start_ms / 1000),
          endSec: Math.max(segment.start_ms / 1000, segment.end_ms / 1000),
          text: segment.text,
          speaker: segment.speaker_label,
          speakerConf: segment.speaker_confidence ?? segment.segment_confidence,
          audioId: row.audioIds[0] ?? '',
        }];
      }).filter((seg): seg is NormalizedSegment => Boolean(seg))
        .sort((a, b) => a.startSec - b.startSec);
    },
    [],
  );

  const handleTranscriptUpdate = useCallback((audioId: string, newText: string) => {
    setJobRows((prev) =>
      prev.map((row) => (
        row.audioIds[0] === audioId && row.transcriptDetail
          ? {
            ...row,
            transcriptDetail: {
              ...row.transcriptDetail,
              transcript_text: newText,
            },
          }
          : row
      )),
    );
  }, []);

  const renderTranscriptContent = useCallback(
    (row: AudioJobRow) => {
      const transcriptDetail = row.transcriptDetail;
      if (!transcriptDetail) {
        return <Text type="secondary">音声認識結果なし</Text>;
      }

      const highlightQuery = appliedSearch.trim();
      const isWhisper = isWhisperBatchEngine(transcriptDetail.engine);
      const summary = getWhisperSegmentSummary(transcriptDetail);

      if (isWhisper) {
        const summaryParts: string[] = [];
        if (summary?.segment_count) {
          summaryParts.push(`セグメント: ${summary.segment_count}件`);
        }
        if (summary?.duration_ms) {
          summaryParts.push(`合計 ${(summary.duration_ms / 1000).toFixed(1)}秒`);
        }
        if (summary?.language) {
          summaryParts.push(`言語: ${summary.language}`);
        }
        if (summary?.rtf) {
          summaryParts.push(`RTF: ${summary.rtf}`);
        }
        if (isNoSpeechDetected(transcriptDetail)) {
          summaryParts.push('認識区間なし');
        }
        const segmentsForAudio = buildSegmentsForRow(transcriptDetail, row);
        const formatTime = (seconds: number) => {
          if (!Number.isFinite(seconds)) return '--:--';
          const mm = String(Math.floor(seconds / 60)).padStart(2, '0');
          const ss = String(Math.floor(seconds % 60)).padStart(2, '0');
          return `${mm}:${ss}`;
        };
        return (
          <div>
            <Text>
              {hasRecognizedSpeech(transcriptDetail)
                ? highlightText(transcriptDetail.transcript_text, highlightQuery, DEFAULT_HIGHLIGHT_STYLE)
                : getWhisperTranscriptMessage(transcriptDetail)}
            </Text>
            {summaryParts.length > 0 && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {summaryParts.join(' / ')}
              </Text>
            )}
            {segmentsForAudio.length > 0 ? (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {segmentsForAudio.map((segment) => (
                  <div
                    key={segment.key}
                    style={{
                      border: '1px dashed #d9d9d9',
                      borderRadius: 8,
                      padding: 12,
                      background: '#fafafa',
                    }}
                  >
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      <Space wrap align="center">
                        <Tag color="purple">{segment.speaker ?? 'Unknown'}</Tag>
                        <Text strong>
                          {formatTime(segment.startSec)} - {formatTime(segment.endSec)}
                        </Text>
                        {segment.speakerConf && (
                          <Text type="secondary">
                            conf {(segment.speakerConf * 100).toFixed(1)}%
                          </Text>
                        )}
                      </Space>
                      <Text>
                        {segment.text
                          ? highlightText(segment.text, highlightQuery, DEFAULT_HIGHLIGHT_STYLE)
                          : '...'}
                      </Text>
                      {activeDeviceId && (
                        row.isGrouped ? (
                          segment.clipSlices && segment.clipSlices.length > 0 ? (
                            <GroupedSegmentAudioPlayer
                              deviceId={activeDeviceId}
                              clips={segment.clipSlices}
                              label="セグメント音声"
                            />
                          ) : null
                        ) : (
                          <SegmentAudioPlayer
                            audioId={segment.audioId}
                            deviceId={activeDeviceId}
                            startSec={segment.startSec}
                            endSec={segment.endSec}
                            label="セグメント音声"
                          />
                        )
                      )}
                    </Space>
                  </div>
                ))}
              </div>
            ) : (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                {transcriptDetail.segments ? 'この音声に対応するセグメントはありません' : 'セグメント詳細を読み込み中...'}
              </Text>
            )}
            {row.isGrouped && (
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Series 全体の再生は右側プレイヤーを使用
              </Text>
            )}
          </div>
        );
      }

      return activeDeviceId ? (
        <AudioTranscriptionEditor
          audioId={row.audioIds[0]}
          deviceId={activeDeviceId}
          transcription={transcriptDetail.transcript_text || ''}
          confidence={transcriptDetail.confidence}
          highlightQuery={appliedSearch}
          onUpdate={(newTranscription) => {
            handleTranscriptUpdate(row.audioIds[0], newTranscription);
          }}
        />
      ) : (
        <Text type="secondary">デバイス情報を取得できませんでした</Text>
      );
    },
    [activeDeviceId, appliedSearch, buildSegmentsForRow, handleTranscriptUpdate],
  );



  return (
    <>
      <DateRangeSelector
        onRangeChange={handleDateRangeChange}
        onModeChange={handleModeChange}
        value={currentRange}
        showPreset={false}
      />

      <Card>
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <Title level={3} style={{ margin: 0 }}>
              <SoundOutlined /> 音声データ (合計 {audioTotal}件)
            </Title>
            <Space>
              <Search
                placeholder="音声認識結果を検索"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                style={{ width: 300 }}
                prefix={<SearchOutlined />}
              />
              <Button 
                icon={<ReloadOutlined />}
                onClick={() => {
                  if (activeDeviceId) {
                    fetchAudioData(activeDeviceId, currentRange, currentPage, pageSize, appliedSearch);
                  }
                }}
                loading={loading}
              >
                更新
              </Button>
            </Space>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Text>音声認識結果:</Text>
            <Button.Group>
              <Button 
                type={transcriptsFilter === 'all' ? 'primary' : 'default'}
                onClick={() => setTranscriptsFilter('all')}
              >
                全て
              </Button>
              <Button 
                type={transcriptsFilter === 'with' ? 'primary' : 'default'}
                onClick={() => setTranscriptsFilter('with')}
              >
                あり
              </Button>
              <Button 
                type={transcriptsFilter === 'without' ? 'primary' : 'default'}
                onClick={() => setTranscriptsFilter('without')}
              >
                なし
              </Button>
            </Button.Group>
          </div>
        </div>

        {deviceNotFound ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="指定されたデバイスが見つかりません"
          >
            <Button type="primary" onClick={() => navigate('/devices')}>
              デバイス一覧へ
            </Button>
          </Empty>
        ) : !activeDeviceId ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="デバイスを選択してください"
          />
        ) : filteredData.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="音声データがありません"
          />
        ) : (
          <List
            dataSource={filteredData}
            pagination={{
              current: currentPage,
              pageSize,
              total: audioTotal,
              showSizeChanger: false,
              showQuickJumper: true,
              pageSizeOptions: ['10'],
              onChange: (page, size) => handlePaginationChange(page, size || pageSize),
              onShowSizeChange: (page, size) => handlePaginationChange(page, size),
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}件`,
            }}
            renderItem={(item) => {
              return (
              <List.Item
                actions={[
                  <div style={{ width: 300 }}>
                    {activeDeviceId && (
                      item.isGrouped ? (
                        <JobAudioPlayer
                          deviceId={activeDeviceId}
                          sources={item.sourceAudios.map((audio) => ({
                            id: audio.id,
                            durationMs: audio.duration_ms,
                          }))}
                        />
                      ) : (
                        <AudioPlayer
                          audioId={item.audioIds[0]}
                          deviceId={activeDeviceId}
                          duration={item.durationMs / 1000}
                        />
                      )
                    )}
                  </div>
                ]}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Text strong>
                        <Link to={`/devices/${activeDeviceId}/audio-series/${item.key}`}>
                          {formatJstDateTime(item.startedAt)}
                        </Link>
                      </Text>
                      <Tag color="blue">Series {item.key.slice(0, 8)}</Tag>
                      <Tag color="blue">{(item.durationMs / 1000).toFixed(1)}秒</Tag>
                      <Tag color="geekblue">{item.codec.toUpperCase()}</Tag>
                      <Tag color="default">Run {item.runCount}</Tag>
                      {renderFormatTag(item)}
                    </Space>
                  }
                  description={
                    <div>
                      {renderJobStructure(item)}
                      {renderTranscriptContent(item)}
                    </div>
                  }
                />
              </List.Item>
            );
            }}
          />
        )}
      </Card>
      
      {showSpinner && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(255,255,255,0.6)',
          zIndex: 9999
        }}>
          <Spin size="large" />
          <div style={{ marginLeft: 16 }}>音声データを読み込み中...</div>
        </div>
      )}
    </>
  );
};

export default AudioPage;
